import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { FC, useMemo } from "react";
import { useRecoilValue } from "recoil";

import {
  getStationComposedId,
  IRideDto,
  ridesDraggingStationsTempIdsAtom,
  RideStationPanelMode,
  useRideAllStationsCollapsed,
} from "@ryde/shared";

import {
  RideStationsHeader,
  RideStationsPanelStyled,
  RideStationWrapper,
  RydeStationBulkActions,
} from "./parts";

interface IRideStationPanel {
  rideDto: IRideDto;
  mode: RideStationPanelMode;
}

export const RideStationsPanel: FC<IRideStationPanel> = ({ rideDto, mode }) => {
  const dragStationComposedIds = useRecoilValue(
    ridesDraggingStationsTempIdsAtom,
  );

  const sortableComposedIds = useMemo(() => {
    const metadataComposedIds =
      rideDto.metadata?.stationsInfo?.map(getStationComposedId) ?? [];

    if (!dragStationComposedIds?.length) {
      return metadataComposedIds;
    }

    if (dragStationComposedIds.length !== metadataComposedIds.length) {
      return metadataComposedIds;
    }

    return dragStationComposedIds;
  }, [dragStationComposedIds, rideDto.metadata?.stationsInfo]);

  const allStationsCollapsed = useRideAllStationsCollapsed(rideDto);

  const isBulkActionsPanelVisible = useMemo(() => {
    return mode === RideStationPanelMode.CRUD_TAB;
  }, [mode]);

  return (
    Boolean(rideDto) && (
      <RideStationsPanelStyled>
        <RideStationsHeader
          rideDto={rideDto}
          allStationsCollapsed={allStationsCollapsed}
        />
        {isBulkActionsPanelVisible && (
          <RydeStationBulkActions rideDto={rideDto} />
        )}
        {Boolean(sortableComposedIds.length) && (
          <SortableContext
            items={sortableComposedIds}
            strategy={verticalListSortingStrategy}
          >
            {sortableComposedIds.map((stationComposedId, index) => {
              return (
                <RideStationWrapper
                  key={stationComposedId}
                  stationComposedId={stationComposedId}
                  rideDto={rideDto}
                  isFirstStation={index === 0}
                  isLastStation={index === sortableComposedIds.length - 1}
                  mode={mode}
                  index={index}
                />
              );
            })}
          </SortableContext>
        )}
      </RideStationsPanelStyled>
    )
  );
};
